import React, { useState } from 'react';
import {
  Typography,
  Grid,
  makeStyles,
  RadioGroup,
  FormControlLabel,
  Radio,
  LinearProgress,
} from '@material-ui/core';
import { Button, defectColors } from '@clef/client-library';
import { useMount } from '@clef/client-library';
import { useDefectSelector } from '@/store/defectState/actions';
import CreateDefectDialog from '@/pages/DefectBook/DefectList/CreateDefectDialog';
import AddIcon from '@material-ui/icons/Add';

const useStyles = makeStyles(theme => ({
  optionItem: {
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    paddingLeft: 10,
    paddingRight: theme.spacing(5),
    '&:hover': {
      backgroundColor: 'rgba(9, 30, 66, 0.08)',
    },
    // overwrites
    marginLeft: 0,
    marginRight: 0,
  },
  textButton: {
    minWidth: 'auto',
    marginRight: theme.spacing(2),
  },
  dropdownPanel: {
    minWidth: 150,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  dropdownContainer: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 8,
  },
  postRequestMask: {
    position: 'absolute',
    height: '100%',
    width: '100%',
    zIndex: 100,
    background: 'rgba(255,255,255,0.7)',
  },
  createClassButton: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    marginLeft: theme.spacing(1),
    textTransform: 'none',
    color: theme.palette.greyModern[500],
  },
}));

export interface SetClassDropdownProps {
  toggleDropdown: (open: boolean) => void;
  enableLinearProgress?: boolean;
  setClass?: (defectId: number, selectedOption: string) => any;
  onSetClassSuccess?: () => void;
}

const INITIAL_SELECTED_OPTION = '';

const SetClassDropdown: React.FC<SetClassDropdownProps> = ({
  toggleDropdown,
  enableLinearProgress,
  setClass,
  onSetClassSuccess,
}) => {
  const styles = useStyles();
  const isMount = useMount();

  const [selectedOption, setSelectedOption] = useState<string>(INITIAL_SELECTED_OPTION);
  const [applyingClass, setApplyingClass] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);

  const defects = useDefectSelector();
  const defectNames = defects.map(d => d.name);
  const defectNameToId = defects.reduce((obj: Record<string, number>, defect) => {
    obj[defect.name] = defect.id;
    return obj;
  }, {});

  return (
    <div className={styles.dropdownContainer} data-testid="set-class-dropdown">
      {enableLinearProgress && applyingClass && (
        <div className={styles.postRequestMask}>
          <LinearProgress />
        </div>
      )}
      <div className={styles.dropdownPanel}>
        {/* Create new class button */}
        {/* <Box> */}
        <Button
          id="create-new-class-btn"
          size="small"
          className={styles.createClassButton}
          startIcon={<AddIcon />}
          onClick={() => setOpenCreateDialog(true)}
        >
          {t('Create new class')}
        </Button>
        {/* </Box> */}
        {/* Radio group for class selection */}
        <RadioGroup
          value={selectedOption || null}
          onChange={(_, newValue: string) => setSelectedOption(newValue)}
        >
          {defectNames
            .sort((a, b) => a.localeCompare(b))
            .map(option => (
              <FormControlLabel
                className={styles.optionItem}
                key={option}
                value={option}
                control={<Radio size="small" color="primary" />}
                label={<Typography variant="body2">{option}</Typography>}
                data-testid={`class-option-${option}`}
              />
            ))}
        </RadioGroup>
        {/* Apply / Clear button */}
        <Grid container justifyContent="flex-end">
          <Button
            id="apply-class-btn"
            color="primary"
            size="small"
            className={styles.textButton}
            disabled={!selectedOption}
            onClick={async () => {
              // TODO: Implement the click logic
              if (enableLinearProgress) {
                setApplyingClass(true);
              }
              setClass && (await setClass(defectNameToId[selectedOption], selectedOption));
              if (enableLinearProgress) {
                isMount() && setApplyingClass(false);
              }
              onSetClassSuccess?.();
              toggleDropdown(false);
            }}
          >
            {t('Apply')}
          </Button>
          <Button
            color="secondary"
            size="small"
            className={styles.textButton}
            disabled={!selectedOption}
            onClick={() => setSelectedOption(INITIAL_SELECTED_OPTION)}
            id="clear-class-btn"
          >
            {t('Clear')}
          </Button>
        </Grid>
      </div>
      {/* Create Defect Dialog */}
      {openCreateDialog && (
        <CreateDefectDialog
          onClose={() => setOpenCreateDialog(false)}
          nextDefectIndex={(defects?.length ?? 0) % defectColors.length}
          onCreateSuccess={() => {
            setOpenCreateDialog(false);
            // Optionally refresh the defects list or close the dropdown
            // The defects list will be automatically updated via the store
          }}
        />
      )}
    </div>
  );
};

export default SetClassDropdown;
